<template>
  <auth-shell>
    <p class="auth-form__eyebrow">
      Tu cuenta está lista
    </p>
    <h1 class="auth-form__title auth-form__title--with-lead">
      ¡Bienvenido{{ firstName ? `, ${firstName}` : '' }}!
    </h1>
    <p class="auth-form__lead">
      Elige cómo quieres empezar. Puedes cambiar de plan cuando quieras desde Mi membresía.
    </p>

    <div
      class="welcome-plans"
      role="radiogroup"
      aria-label="Plan"
    >
      <div
        role="radio"
        tabindex="0"
        :aria-checked="selectedPlan === 'free' ? 'true' : 'false'"
        :aria-label="`Plan ${freePlan ? freePlan.name : 'FREE'}, gratis`"
        :class="['welcome-plan', { 'welcome-plan--selected': selectedPlan === 'free' }]"
        @click="selectedPlan = 'free'"
        @keydown.enter.prevent="selectedPlan = 'free'"
        @keydown.space.prevent="selectedPlan = 'free'"
      >
        <header class="welcome-plan__head">
          <span class="welcome-plan__title">
            <span class="welcome-plan__radio" />
            <span class="welcome-plan__name">{{ freePlan ? freePlan.name : 'FREE' }}</span>
          </span>
          <span class="welcome-plan__price">Gratis</span>
        </header>
        <p class="welcome-plan__tagline">
          {{ freePlan && freePlan.tagline ? freePlan.tagline : 'Para empezar a cotizar' }}
        </p>
        <ul
          v-if="freeHighlights.length"
          class="welcome-plan__list"
        >
          <li
            v-for="item in freeHighlights"
            :key="item"
          >
            {{ item }}
          </li>
        </ul>
      </div>

      <div
        role="radio"
        tabindex="0"
        :aria-checked="selectedPlan === 'pro' ? 'true' : 'false'"
        :aria-label="`Plan ${proPlan ? proPlan.name : 'PRO'}${proPlan ? `, ${formatMoney(proPlan.price)} al mes` : ''}`"
        :class="['welcome-plan', { 'welcome-plan--selected': selectedPlan === 'pro' }]"
        @click="selectedPlan = 'pro'"
        @keydown.enter.prevent="selectedPlan = 'pro'"
        @keydown.space.prevent="selectedPlan = 'pro'"
      >
        <header class="welcome-plan__head">
          <span class="welcome-plan__title">
            <span class="welcome-plan__radio" />
            <span class="welcome-plan__name">{{ proPlan ? proPlan.name : 'PRO' }}</span>
          </span>
          <span
            v-if="proPlan"
            class="welcome-plan__price"
          >{{ formatMoney(proPlan.price) }} al mes</span>
        </header>
        <p class="welcome-plan__tagline">
          {{ proPlan && proPlan.tagline ? proPlan.tagline : 'Todo incluido' }}
        </p>
        <ul
          v-if="proHighlights.length"
          class="welcome-plan__list"
        >
          <li
            v-for="item in proHighlights"
            :key="item"
          >
            {{ item }}
          </li>
        </ul>
        <span
          v-if="proBestTerm"
          class="welcome-plan__offer"
        >Ahorra {{ proBestTerm.discount_percent }}% pagando {{ proBestTerm.months }} meses</span>
      </div>
    </div>

    <b-button
      class="auth-submit"
      block
      @click="continueWithPlan"
    >
      <span>Continuar</span>
      <feather-icon
        icon="ArrowRightIcon"
        size="18"
      />
    </b-button>
  </auth-shell>
</template>

<script>
import { BButton } from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import { getHomeRouteForLoggedInUser, getUserData } from '@/auth/utils'
import { markWelcomeSeen } from '@/auth/session'
import AuthShell from '@/components/auth/AuthShell.vue'

const PRO_PRIORITY = [
  'suppliers_visible_from_start',
  'comparative_suppliers',
  'purchase_management',
  'budgets_access',
  'max_users',
  'income_tracking',
  'reports_access',
  'agreement_prices',
  'active_projects',
  'free_quotes',
]

const describeLimit = (feature, value) => {
  if (value === null || value === undefined || value === '') return `${feature.name}: sin límite`
  return `${feature.name}: hasta ${value}`
}

export default {
  components: {
    AuthShell,
    BButton,
  },

  data() {
    return {
      userData: getUserData() || {},
      selectedPlan: 'pro',
    }
  },

  computed: {
    ...mapGetters('memberships', { plans: 'membershipPlans', catalog: 'membershipCatalog' }),

    firstName() {
      return String(this.userData.name || '').trim().split(/\s+/)[0] || ''
    },
    freePlan() {
      return this.plans.find(plan => plan.key === 'free') || null
    },
    proPlan() {
      return this.plans.find(plan => plan.key === 'pro') || null
    },
    proBestTerm() {
      return (this.proPlan?.term_options || []).reduce((best, option) => (
        Number(option.discount_percent) > Number(best?.discount_percent || 0) ? option : best
      ), null)
    },
    features() {
      return (this.catalog || []).flatMap(category => category.features || [])
    },
    freeHighlights() {
      if (!this.freePlan) return []
      return this.features
        .filter(feature => feature.kind === 'limit' && this.freePlan.features[feature.key] !== null && this.freePlan.features[feature.key] !== undefined)
        .slice(0, 3)
        .map(feature => describeLimit(feature, this.freePlan.features[feature.key]))
    },
    proHighlights() {
      if (!this.freePlan || !this.proPlan) return []
      const rank = feature => {
        const index = PRO_PRIORITY.indexOf(feature.key)
        return index === -1 ? PRO_PRIORITY.length : index
      }
      return this.features
        .filter(feature => this.proPlan.features[feature.key] !== this.freePlan.features[feature.key])
        .filter(feature => feature.kind === 'limit' || this.proPlan.features[feature.key] === true)
        .sort((a, b) => rank(a) - rank(b))
        .slice(0, 5)
        .map(feature => (feature.kind === 'limit' ? describeLimit(feature, this.proPlan.features[feature.key]) : feature.name))
    },
  },

  created() {
    this.$store.dispatch('memberships/getMembershipPlans').catch(() => {})
  },

  methods: {
    formatMoney(value) {
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(Number(value || 0))
    },
    finish() {
      markWelcomeSeen(this.userData.id)
    },
    continueFree() {
      this.finish()
      this.$router.replace(getHomeRouteForLoggedInUser(this.userData))
    },
    choosePro() {
      this.finish()
      this.$router.replace({ name: 'membership', query: { plan: 'pro' } })
    },
    continueWithPlan() {
      if (this.selectedPlan === 'pro') {
        this.choosePro()
      } else {
        this.continueFree()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../components/auth/auth-page.scss';

.welcome-plans {
  display: grid;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.welcome-plan {
  padding: 0.9rem 1rem;
  border: 1px solid $auth-line;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #b8c0cc;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(29, 78, 216, 0.25);
  }
}

.welcome-plan--selected,
.welcome-plan--selected:hover {
  border-color: $auth-green;
  box-shadow: 0 0 0 3px rgba(43, 217, 85, 0.14);
}

.welcome-plan__title {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
}

.welcome-plan__radio {
  position: relative;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border: 2px solid #b8c0cc;
  border-radius: 50%;
  transition: border-color 0.15s ease;

  .welcome-plan--selected & {
    border-color: $auth-green;

    &::after {
      content: '';
      position: absolute;
      top: 3px;
      left: 3px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: $auth-green;
    }
  }
}

.welcome-plan__head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
}

.welcome-plan__name {
  font-size: 1rem;
  font-weight: 700;
  color: $auth-ink;
}

.welcome-plan__price {
  font-size: 0.9rem;
  font-weight: 700;
  color: #16a34a;
}

.welcome-plan__tagline {
  margin: 0.15rem 0 0.4rem;
  font-size: 0.82rem;
  color: $auth-muted;
}

.welcome-plan__list {
  margin: 0;
  padding-left: 1.1rem;
  font-size: 0.84rem;
  line-height: 1.45;
  color: #334155;

  li + li {
    margin-top: 0.15rem;
  }
}

.welcome-plan__offer {
  display: inline-block;
  margin-top: 0.55rem;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  background: rgba(43, 217, 85, 0.14);
  font-size: 0.75rem;
  font-weight: 700;
  color: #15803d;
}
</style>
